import { useToast } from "hooks/useToast";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import randomBytes from "randombytes";
import {
  useGetAllConnectionsQuery,
  useLazyGetConnectionAuthUrlQuery,
} from "store/apis/booksConnections";
import { BackendError } from "types/utils/error";
import { useEffect, useState } from "react";
import { TRANSACTION_PULL_TYPE } from "types/contants/transactionPullTypes";
import {
  BANK_CONNECTION_PROVIDER,
  CODE_RECEIVED,
  CONNECTION_AUTHENTICATION_FAILED,
  CONNECTION_AUTHENTICATION_SUCCESS,
} from "constants/dataSource";
import { useAppDispatch } from "./useAppDispatch";
import { setSvbConnectModal } from "store/slices/svbConnect";
import { useAppSelector } from "./useAppSelector";

export type ConnectionLocalState = {
  entityId: string;
  groupId: string;
  connectionId: string;
  invokedFrom?: string;
  txn_pull_date: string;
  txn_pull_choice: TRANSACTION_PULL_TYPE;
};

export type onConnectArgs = {
  connectionId: string;
  entityIdInArg?: string;
  invokedFrom?: string;
  type?: "POPUP" | "REDIRECT";
  entityItemId?: string;
  state?: Pick<ConnectionLocalState, "txn_pull_choice" | "txn_pull_date">;
};

type onConnectPopupArgs = {
  connectionId: string;
  entityId: string;
  entityItemId?: string;
  returnCodeWithoutExchange?: boolean;
};

export const useBankConnect = ({
  onConnectSuccess,
  connectionId: watchConnectionId,
}: {
  onConnectSuccess?: (code?: string | null) => void;
  connectionId?: string;
} = {}) => {
  const [connecting, setConnecting] = useState(false);
  const { alertToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const dispatch = useAppDispatch();
  const isSvbConnectionSuccessFull = useAppSelector(
    (state) => state.svbConnect.connection_successful
  );
  const code = useAppSelector((state) => state.svbConnect.code);
  const codeOnlyMode = useAppSelector(
    (state) => state.svbConnect.code_only_mode
  );
  const svbConnectionId = useAppSelector(
    (state) => state.svbConnect.connectionId
  );

  const { data: connections } = useGetAllConnectionsQuery(
    {
      groupId: groupId!,
      entityId: entityId!,
    },
    { skip: !groupId || !entityId }
  );

  const [getAuthUrl, { isLoading, originalArgs }] =
    useLazyGetConnectionAuthUrlQuery();

  useEffect(() => {
    if (
      isSvbConnectionSuccessFull &&
      code &&
      codeOnlyMode &&
      svbConnectionId === watchConnectionId
    ) {
      onConnectSuccess?.(code);
      dispatch(
        setSvbConnectModal({
          isOpen: false,
          connectionId: undefined,
          entityId: undefined,
          groupId: undefined,
          txn_pull_choice: undefined,
          txn_pull_date: undefined,
          connection_successful: false,
          code: undefined,
          code_only_mode: false,
        })
      );
    }
  }, [
    isSvbConnectionSuccessFull,
    code,
    codeOnlyMode,
    svbConnectionId,
    watchConnectionId,
    dispatch,
  ]);

  const onConnect = async ({
    connectionId,
    entityIdInArg,
    invokedFrom,
    state,
    type = "REDIRECT",
    entityItemId,
  }: onConnectArgs) => {
    if (groupId && (entityIdInArg || entityId)) {
      try {
        const stateKey = randomBytes(16).toString("hex");
        localStorage.setItem("connection_oauth_state_key", stateKey);

        localStorage.setItem(
          stateKey,
          JSON.stringify({
            entityId: entityIdInArg || entityId,
            groupId,
            connectionId,
            invokedFrom,
            ...state,
          } as ConnectionLocalState)
        );

        const connection = connections?.find(
          (conn) => conn.uuid === connectionId
        );

        const svb =
          connection?.connection_provider === BANK_CONNECTION_PROVIDER.SVB;

        if (svb) {
          return dispatch(
            setSvbConnectModal({
              isOpen: true,
              connectionId,
              entityId: entityIdInArg || entityId,
              groupId,
              txn_pull_choice: state?.txn_pull_choice,
              txn_pull_date: state?.txn_pull_date,
              code_only_mode: type === "POPUP",
            })
          );
        }

        const { authorization_url } = await getAuthUrl({
          groupId,
          connectionId,
          entityId: (entityIdInArg || entityId)!,
          state: stateKey,
          entityItemId,
        }).unwrap();

        if (type === "POPUP") {
          return authorization_url;
        }

        window.location.href = authorization_url;
      } catch (error) {
        alertToast({ message: (error as BackendError).data?.error?.message });
      }
    }
  };

  /**
   * Initiates a bank connection flow in a popup window.
   *
   * Opens a new browser window for OAuth authentication and listens for
   * connection status messages. For SVB connections, delegates to onConnect
   * with popup type instead.
   *
   * Uses localStorage with storage event listener for cross-window communication
   * since window.opener is lost after cross-origin OAuth redirects.
   *
   * @param connectionId - UUID of the bank connection to authenticate
   * @param entityId - UUID of the entity initiating the connection
   * @param entityItemId - Optional entity item identifier
   * @param returnCodeWithoutExchange - When false, bypass account mapper flow and returns the OAuth code with exchanging it for tokens (defaults to true)
   * @returns Promise that resolves when connection completes or fails
   */
  const onConnectPopup = async ({
    connectionId,
    entityId,
    entityItemId,
    returnCodeWithoutExchange = true,
  }: onConnectPopupArgs) => {
    const connection = connections?.find((conn) => conn.uuid === connectionId);

    const svb =
      connection?.connection_provider === BANK_CONNECTION_PROVIDER.SVB;

    if (svb) {
      return onConnect({
        connectionId,
        entityIdInArg: entityId,
        type: "POPUP",
        entityItemId,
      });
    }

    setConnecting(true);

    // Clear any previous connection result
    localStorage.removeItem("connection_popup_result");

    try {
      const authUrl = await onConnect({
        connectionId: connectionId,
        entityIdInArg: entityId,
        type: "POPUP",
        entityItemId,
      });

      if (authUrl) {
        const handle = window.open(
          `${window.location.origin}/redirect?store=${JSON.stringify({
            connection_confirmation_config: {
              return_code_without_exchange: returnCodeWithoutExchange,
            },
          })}&url=${encodeURIComponent(
            // @ts-ignore
            authUrl
          )}`,
          "BrexConnect"
        );

        if (handle) {
          const code = await new Promise<string | null>((resolve, reject) => {
            // Listen for storage events (works across windows on same origin)
            const handleStorageChange = (ev: StorageEvent) => {
              if (ev.key === "connection_popup_result" && ev.newValue) {
                const result = JSON.parse(ev.newValue);
                localStorage.removeItem("connection_popup_result");
                window.removeEventListener("storage", handleStorageChange);

                if (result.status === CONNECTION_AUTHENTICATION_SUCCESS) {
                  resolve(null);
                } else if (result.status === CONNECTION_AUTHENTICATION_FAILED) {
                  reject(result.message || "Connection failed");
                } else if (result.status === CODE_RECEIVED) {
                  resolve(result.code);
                }
              }
            };

            window.addEventListener("storage", handleStorageChange);

            // Also listen for postMessage as fallback (in case opener is preserved)
            const handleMessage = (ev: MessageEvent) => {
              if (ev.data === CONNECTION_AUTHENTICATION_SUCCESS) {
                window.removeEventListener("message", handleMessage);
                window.removeEventListener("storage", handleStorageChange);
                resolve(null);
              }

              if (ev.data === CONNECTION_AUTHENTICATION_FAILED) {
                window.removeEventListener("message", handleMessage);
                window.removeEventListener("storage", handleStorageChange);
                reject("Connection failed");
              }

              if (
                typeof ev.data === "string" &&
                ev.data.startsWith(CODE_RECEIVED)
              ) {
                const code = ev.data.split(":")[1];
                window.removeEventListener("message", handleMessage);
                window.removeEventListener("storage", handleStorageChange);
                resolve(code);
              }
            };

            window.addEventListener("message", handleMessage);
          });

          onConnectSuccess?.(code);
          handle.close();
        }
      }
    } catch (error: any) {
      alertToast(
        { message: error?.response?.data?.error?.message || error },
        error
      );
    } finally {
      setConnecting(false);
    }
  };

  return {
    isLoading: isLoading || connecting,
    originalArgs,
    onConnect,
    onConnectPopup,
  };
};
